import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowClockwise, Clipboard, Printer } from "react-bootstrap-icons";
import {
  buildClipboardPayload,
  writeClipboard,
} from "../EOYReportAssistant/ReportCard";
import { printReportNodes } from "../EOYReportAssistant/printReports";
import ScoresTable from "../EOYReportAssistant/ScoresTable";
import ScoresVisibilityToggle from "../EOYReportAssistant/ScoresVisibilityToggle";
import PrintHeader from "../PrintHeader";
import { updateNewClassInsightsReport } from "../../services/APIClient";
import { reportHasContent } from "./useEnhancedReportWorkflow";
import "../PrintStudentResults.css";
import "../EOYReportAssistant/EOYReportAssistant.css";

function clearTimer(timerRef) {
  if (!timerRef.current) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

function withoutInstanceSuffix(name) {
  return String(name || "").replace(/\s+instance\s*$/i, "");
}

/**
 * New Class Insights report card. The only enhanced report type with a save
 * endpoint (updateNewClassInsightsReport), so this is the one card with an
 * editable report textarea — modeled on ReportCard.js's auto-save pattern.
 * Teachers can switch between the AI-generated version and their saved
 * edited version without changing either one.
 */
function NewClassInsightsReportCard({
  report,
  className,
  onReportNodeReady,
  onRegenerateReport,
  regenerating,
  summaryMode: controlledSummaryMode,
  onSummaryModeChange,
  scoresVisible: controlledScoresVisible,
  onScoresVisibilityChange,
}) {
  const [editedText, setEditedText] = useState("");
  const [localSummaryMode, setLocalSummaryMode] = useState("generated");
  const [hasSavedEditedSummary, setHasSavedEditedSummary] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [showLearningOverview, setShowLearningOverview] = useState(true);
  const reportShellRef = useRef(null);
  const textareaRef = useRef(null);
  const activePrintCleanupRef = useRef(null);
  const saveStatusTimer = useRef(null);
  const pendingSaveTimer = useRef(null);
  const copyStatusTimer = useRef(null);
  const saveChainRef = useRef(Promise.resolve());
  const latestEditedTextRef = useRef("");
  const mountedRef = useRef(true);

  const studentId = report?.student_id;
  const sourceAcademicYear = report?.source_academic_year;
  const destinationAcademicYear = report?.destination_academic_year;
  const generatedText = report?.report || "";
  const savedEditedText = report?.edited_report || "";
  const activeSummaryMode = controlledSummaryMode || localSummaryMode;
  const scoresVisible =
    typeof controlledScoresVisible === "boolean"
      ? controlledScoresVisible
      : showLearningOverview;
  const visibleText =
    activeSummaryMode === "generated" ? generatedText : editedText;

  useEffect(() => {
    const hasEditedText = Boolean(savedEditedText.trim());
    const initialText = hasEditedText ? savedEditedText : generatedText;
    setEditedText(initialText);
    latestEditedTextRef.current = initialText;
    setLocalSummaryMode(hasEditedText ? "edited" : "generated");
    setHasSavedEditedSummary(hasEditedText);
    setSaveStatus("");
  }, [generatedText, report?.student_id, savedEditedText]);

  // The textarea has resize: none + overflow: hidden in CSS, so it only
  // fits its content if something grows its height to match — otherwise
  // anything past a few lines is silently clipped with no way to scroll
  // to it. Mirrors ReportCard.js's resizeTextarea/useLayoutEffect pattern.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [activeSummaryMode, visibleText]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pendingSaveTimer.current) {
        clearTimer(pendingSaveTimer);
        persistEdit(latestEditedTextRef.current);
      }
      clearTimer(saveStatusTimer);
      clearTimer(copyStatusTimer);
      activePrintCleanupRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    if (!report?.success || !studentId || !onReportNodeReady) return undefined;
    onReportNodeReady(studentId, reportShellRef.current);
    return () => onReportNodeReady(studentId, null);
  }, [onReportNodeReady, studentId, report?.success]);

  function queueSave(nextText) {
    if (!studentId) return Promise.resolve();

    const saveTask = saveChainRef.current
      .catch(() => {})
      .then(() =>
        updateNewClassInsightsReport(studentId, {
          reportText: nextText,
          sourceAcademicYear,
          destinationAcademicYear,
        })
      );

    saveChainRef.current = saveTask.catch(() => {});
    return saveTask;
  }

  async function persistEdit(nextText) {
    try {
      await queueSave(nextText);
      if (!mountedRef.current) return;
      setSaveStatus("Saved");
      setHasSavedEditedSummary(true);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Could not save New Class Insights edit:", err);
      setSaveStatus("Save failed");
    }
    clearTimer(saveStatusTimer);
    saveStatusTimer.current = window.setTimeout(() => {
      setSaveStatus("");
      saveStatusTimer.current = null;
    }, 1800);
  }

  if (!report) return null;

  const displayId = report.display_name || report.student_id;
  const isRegenerationAvailable = report.can_regenerate === true;

  if (!report.success) {
    return (
      <div className="eoy-report-card eoy-report-card-error">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <span className="eoy-report-badge eoy-report-badge-error">
            {isRegenerationAvailable ? "Update available" : "Failed"}
          </span>
        </div>
        <p className="eoy-report-text">
          {report.error?.message || "Report could not be generated."}
        </p>
        {onRegenerateReport && studentId && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  const handleTextChange = (event) => {
    const nextText = event.target.value;
    setEditedText(nextText);
    latestEditedTextRef.current = nextText;
    setSaveStatus("Saving...");
    clearTimer(saveStatusTimer);
    clearTimer(pendingSaveTimer);

    pendingSaveTimer.current = window.setTimeout(() => {
      pendingSaveTimer.current = null;
      persistEdit(nextText);
    }, 600);
  };

  const handleTextBlur = () => {
    if (!pendingSaveTimer.current) return;
    clearTimer(pendingSaveTimer);
    persistEdit(latestEditedTextRef.current);
  };

  const handleSummaryModeChange = (nextMode) => {
    setLocalSummaryMode(nextMode);
    onSummaryModeChange?.(nextMode);
  };

  const handleScoresVisibilityChange = (isVisible) => {
    setShowLearningOverview(isVisible);
    onScoresVisibilityChange?.(isVisible);
  };

  const handlePrintReport = () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;
    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes([reportNode]);
  };

  const handleCopyReport = async () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;

    try {
      const copied = await writeClipboard(buildClipboardPayload(reportNode));
      setCopyStatus(copied ? "Copied" : "Copy failed");
    } catch (err) {
      console.warn("Could not copy New Class Insights report:", err);
      setCopyStatus("Copy failed");
    }
    clearTimer(copyStatusTimer);
    copyStatusTimer.current = window.setTimeout(() => {
      setCopyStatus("");
      copyStatusTimer.current = null;
    }, 1800);
  };

  const screeners = (report.scores || []).map((screener) => ({
    ...screener,
    name: withoutInstanceSuffix(screener.name),
  }));
  const hasScores = screeners.length > 0;
  // Placeholders ("No previous assessment summary is available...") already
  // carry real text in report.report, so this only catches a genuinely
  // empty/broken cached record — distinct from a legitimate placeholder.
  const isEmptyReport = !reportHasContent(report);

  return (
    <div className="eoy-report-print-shell" ref={reportShellRef}>
      <div className="eoy-report-card">
        <PrintHeader
          className={className}
          academicYear={destinationAcademicYear}
          assessmentName="New Class Insights"
        />
        <div className="eoy-report-toolbar no-print">
          <span
            className={`eoy-copy-status ${copyStatus ? "" : "eoy-copy-status-hidden"}`}
            aria-live="polite"
          >
            {copyStatus || "Copied"}
          </span>
          {hasScores && (
            <ScoresVisibilityToggle
              label="Show results"
              tooltip="Show or hide the previous-year assessment results"
              checked={scoresVisible}
              onChange={handleScoresVisibilityChange}
            />
          )}
          <button
            aria-label="Copy report to clipboard"
            className="eoy-toolbar-icon-btn"
            onClick={handleCopyReport}
            data-tooltip="Copy report to clipboard"
            type="button"
          >
            <Clipboard aria-hidden="true" size={16} />
          </button>
          <button
            aria-label="Print this report"
            className="eoy-toolbar-icon-btn"
            onClick={handlePrintReport}
            data-tooltip="Print this report"
            type="button"
          >
            <Printer aria-hidden="true" size={16} />
          </button>
        </div>

        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <small>
            {sourceAcademicYear} &rarr; {destinationAcademicYear}
          </small>
        </div>

        {scoresVisible && hasScores && (
          <ScoresTable
            screeners={screeners}
            heading="Previous-year assessment results"
          />
        )}

        <div className="eoy-parent-report-section">
          <div className="nci-learning-overview-heading">
            <h3>New Class Insight</h3>
            <p>
              This insight combines the saved End of Year report from ALPACA
              with any staff notes carried forward from the previous school
              year.
            </p>
          </div>
          <div className="eoy-parent-report-label no-print">
            AI-GENERATED CLASS INSIGHT — EDITS ARE SAVED AUTOMATICALLY
            {hasSavedEditedSummary && (
              <span className="eoy-edited-badge">Edited</span>
            )}
          </div>
          <div className="eoy-summary-toggle-row no-print">
            <span className="eoy-summary-toggle-label">AI Generated</span>
            <label
              className="eoy-summary-switch"
              data-tooltip="Toggle between the AI-generated and edited insight"
            >
              <input
                type="checkbox"
                aria-label="Toggle between the AI-generated and edited insight"
                checked={activeSummaryMode === "edited"}
                onChange={(event) =>
                  handleSummaryModeChange(
                    event.target.checked ? "edited" : "generated",
                  )
                }
              />
              <span className="eoy-summary-slider" />
            </label>
            <span className="eoy-summary-toggle-label">Edited</span>
          </div>
          {isEmptyReport && (
            <p className="eoy-report-text eoy-muted no-print">
              No report content was generated for {displayId}. You can write
              one below, or try regenerating.
            </p>
          )}
          <textarea
            ref={textareaRef}
            className="eoy-report-textarea no-print"
            value={visibleText}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            readOnly={activeSummaryMode === "generated"}
            placeholder={isEmptyReport ? "No report content available yet." : ""}
            rows={6}
          />
          <div className="eoy-report-print-summary" aria-hidden="true">
            {visibleText}
          </div>
          <div className="eoy-summary-status-row no-print" aria-live="polite">
            {activeSummaryMode === "generated" ? (
              <span className="eoy-summary-help">
                Switch to Edited to make changes.
              </span>
            ) : (
              <span
                className={`eoy-save-status ${saveStatus ? "" : "eoy-save-status-hidden"}`}
              >
                {saveStatus || "Saved"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewClassInsightsReportCard;
